import React from 'react';
import { useContext, useState } from 'react';
import { BinStatusContext } from '../../../../../../context/BinStatusContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import {
  BinStatusMutations,
  CreateBinStatusResponse,
  CreateBinStatusVariables,
  UpdateBinStatusResponse,
  UpdateBinStatusVariables,
} from '../../../../../../graphql/binStatus.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { BinStatus, BinStatusInput } from '../../../../../../types/bin';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../types/translation/Translator';
import { Button, CancelButton } from '../../../../../components/Button';
import { Checkbox } from '../../../../../components/Checkbox';
import { ColorPicker } from '../../../../../components/ColorPicker';
import { ActionModal } from '../../../../../components/Modal';
import { TextInput } from '../../../../../components/TextInput';
import { ToastType } from '../../../../../components/Toast';

interface BinStatusModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  binStatus: BinStatus | undefined;
}

export default function BinStatusModal({ open, setOpen, binStatus }: BinStatusModalProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { binStatuses, setBinStatuses } = useContext(BinStatusContext);

  const [input, setInput] = useState(new BinStatusInput(binStatus || { companyId: currentCompany.id }));

  const handleActionCompleted = (binStatus: BinStatus) => {
    setOpen(false);

    setBinStatuses(new Map(binStatuses.set(binStatus.id, binStatus)));
  };

  const [create, { loading: createLoading }] = useMutation<CreateBinStatusResponse, CreateBinStatusVariables>(
    BinStatusMutations.create,
    {
      variables: { binStatuses: [input.forCreate()] },
      onCompleted: response => {
        handleActionCompleted(response.createBinStatus[0]);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.binStatusCreatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateBinStatusResponse, UpdateBinStatusVariables>(
    BinStatusMutations.update,
    {
      variables: { binStatuses: [input.forUpdate()] },
      onCompleted: response => {
        handleActionCompleted(response.updateBinStatus[0]);
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.binStatusUpdatedSuccessfullyToastTitle).singular.upper,
        });
      },
    },
  );

  const loading = createLoading || updateLoading;

  const handleSave = () => {
    if (binStatus) {
      update();
    } else {
      create();
    }
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <Button
        text={t(binStatus ? TranslationKey.update : TranslationKey.create).singular.upper}
        onClick={handleSave}
        loading={loading}
        disabled={!input.validate() || !input.hasChanges(binStatus)}
      />
    </div>
  );

  return (
    <ActionModal
      title={t(binStatus ? TranslationKey.updateBinStatus : TranslationKey.createBinStatus).singular.upper}
      open={open}
      setOpen={setOpen}
      footer={footer}
    >
      <div className='grid gap-4'>
        <TextInput
          mandatory
          value={input.status}
          onChange={value => setInput(input.withStatus(value))}
          label={t(TranslationKey.name).singular.label}
          placeholder={t(TranslationKey.binStatusModalStatusInputPlaceholder).singular.upper}
        />
        <ColorPicker
          color={input.color}
          label={t(TranslationKey.color).singular.upper}
          onChange={color => setInput(input.withColor(color))}
        />
        <Checkbox
          value={input.inboundAllowed || false}
          label={t(TranslationKey.inboundAllowed).singular.upper}
          onChange={value => setInput(input.withInboundAllowed(value))}
        />
        <Checkbox
          value={input.outboundAllowed || false}
          label={t(TranslationKey.outboundAllowed).singular.upper}
          onChange={value => setInput(input.withOutboundAllowed(value))}
        />
      </div>
    </ActionModal>
  );
}
